import { useMobileViewport } from './modules/shared/useMobileViewport'
import { EvaluationHub } from './modules/evaluations/EvaluationHub'
import React, { useState } from 'react'
import { AuthProvider, useAuth } from './modules/auth/AuthContext'
import { AppLayout, ActiveTab } from './modules/shared/AppLayout'
import { CampsView } from './modules/camps/CampsView'
import { CalendarView } from './modules/calendar/CalendarView'
import { SportsView } from './modules/sports/SportsView'
import { HealthView } from './modules/health/HealthView'
import { TeamView } from './modules/team/TeamView'
import { UsersAndPermissionsView } from './modules/admin/UsersAndPermissionsView'
import { AuthStatusView } from './modules/auth/AuthStatusView'
import { LoginView } from './modules/auth/LoginView'
import { ResetPasswordView } from './modules/auth/ResetPasswordView'
import { SetNewPasswordView } from './modules/auth/SetNewPasswordView'
import { AcceptInvitationView } from './modules/auth/AcceptInvitationView'
import { DashboardPreviewView } from './modules/dashboard/DashboardPreviewView'
import { DashboardView } from './modules/dashboard/DashboardView'
import { isSupabaseConfigured } from './config/env'

type AuthScreen = 'login' | 'reset' | 'set-new-password' | 'invitation'

const AppContent: React.FC = () => {
  const { user, loading, authDataError, retryAuthData, signOut } = useAuth()
  const [activeTab, setActiveTab] = useState<ActiveTab>('today')
  const [authScreen, setAuthScreen] = useState<AuthScreen>('login')
  const [demoBypass, setDemoBypass] = useState(false)

  const isConfigured = isSupabaseConfigured()

  // Detección de flujos de enlace: recuperación de contraseña o invitación
  React.useEffect(() => {
    const hash = window.location.hash
    const params = new URLSearchParams(window.location.search)

    if (hash.includes('type=recovery') || params.get('type') === 'recovery' || params.get('action') === 'set-new-password') {
      setAuthScreen('set-new-password')
    } else if (params.get('token')) {
      setAuthScreen('invitation')
    } else if (params.get('action') === 'reset' || window.location.pathname === '/reset-password') {
      setAuthScreen('reset')
    } else if (params.get('event')) {
      setActiveTab('calendar')
    } else if (params.get('view') === 'sports' || params.get('tab') === 'stats' || params.get('sportsTab')) {
      setActiveTab('sports')
    } else if (params.get('view') === 'admin' || params.get('tab') === 'admin') {
      setActiveTab('admin')
    } else if (params.get('view') === 'evaluations') {
      setActiveTab('evaluations')
    } else if (params.get('view') === 'dashboard-preview' || params.get('preview') === 'm8c') {
      setActiveTab('dashboard-preview')
      setDemoBypass(true)
    } else if (params.get('view') === 'dashboard') {
      setActiveTab('dashboard')
    } else if (params.get('view') === 'health' || params.get('tab') === 'health') {
      setActiveTab('health')
    } else if (params.get('view') === 'team' || params.get('tab') === 'team') {
      setActiveTab('team')
    }
  }, [])

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--nav-bg-base)', color: 'var(--nav-text)' }}>
        <div style={{ textAlign: 'center' }}>
          <img src="/branding/logo.png" alt="FEFA" style={{ height: '42px', width: 'auto', marginBottom: '1rem' }} />
          <div style={{ fontSize: '0.88rem', color: 'var(--nav-text-muted)' }}>Cargando Team Spain...</div>
        </div>
      </div>
    )
  }

  // Si falló la carga de perfil o permisos tras autenticar, mostramos pantalla comprensible con reintento y diagnóstico
  if (authDataError) {
    const isDev = import.meta.env.DEV
    return (
      <div className="auth-container">
        <div className="auth-card" style={{ maxWidth: '460px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <img src="/branding/logo.png" alt="FEFA" style={{ height: '36px', width: 'auto' }} />
            <div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
                No se pudieron cargar tus datos de acceso
              </h2>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                {authDataError.step === 'profile' ? 'Perfil de deportista / técnico' : 'Membresías y permisos de equipo'}
              </span>
            </div>
          </div>

          <p style={{ fontSize: '0.86rem', color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.5 }}>
            Hemos validado tu sesión para <strong>{authDataError.email || user?.email}</strong>, pero se produjo una incidencia al consultar la información de tu cuenta. Por favor, reintenta la conexión o cierra sesión si el problema persiste.
          </p>

          <details
            style={{
              marginBottom: '1.25rem',
              fontSize: '0.8rem',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.5rem 0.75rem',
              backgroundColor: 'var(--bg-card)',
            }}
            open={isDev}
          >
            <summary style={{ cursor: 'pointer', fontWeight: 600, color: 'var(--text-main)' }}>
              Detalle técnico (diagnóstico de desarrollo)
            </summary>
            <div
              style={{
                marginTop: '0.5rem',
                padding: '0.5rem',
                backgroundColor: 'var(--status-danger-bg)',
                color: 'var(--status-danger-text)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                wordBreak: 'break-word',
              }}
            >
              {authDataError.message}
            </div>
          </details>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => signOut()}
            >
              Cerrar sesión
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => retryAuthData()}
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Si no hay usuario autenticado y no se ha solicitado explorar la demo local sin backend
  if (!user && !demoBypass && isConfigured) {
    if (authScreen === 'set-new-password') {
      return <SetNewPasswordView onSuccess={() => setAuthScreen('login')} />
    }
    if (authScreen === 'reset') {
      return <ResetPasswordView onBackToLogin={() => setAuthScreen('login')} />
    }
    if (authScreen === 'invitation') {
      return <AcceptInvitationView onBackToLogin={() => setAuthScreen('login')} />
    }
    return (
      <LoginView
        onSwitchToReset={() => setAuthScreen('reset')}
        onSwitchToInvitation={() => setAuthScreen('invitation')}
      />
    )
  }

  // Si no está configurado Supabase aún o si se solicitó ver login explícitamente
  if (!user && !demoBypass && !isConfigured) {
    // Si estamos en un preview o desarrollo local sin backend Supabase aún configurado,
    // mostramos una opción clara para ver el login o explorar las pantallas de M1
    return (
      <div>
        {authScreen === 'set-new-password' && (
          <SetNewPasswordView onSuccess={() => setAuthScreen('login')} />
        )}
        {authScreen === 'reset' && (
          <ResetPasswordView onBackToLogin={() => setAuthScreen('login')} />
        )}
        {authScreen === 'invitation' && (
          <AcceptInvitationView onBackToLogin={() => setAuthScreen('login')} />
        )}
        {authScreen === 'login' && (
          <div>
            <LoginView
              onSwitchToReset={() => setAuthScreen('reset')}
              onSwitchToInvitation={() => setAuthScreen('invitation')}
            />
            <div style={{ position: 'fixed', bottom: '16px', right: '16px', zIndex: 100 }}>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: '0.78rem', boxShadow: 'var(--shadow-card)' }}
                onClick={() => setDemoBypass(true)}
              >
                Explorar estructura visual (Sin sesión activa)
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (authScreen === 'invitation') return <AcceptInvitationView onBackToLogin={() => setAuthScreen('login')} />

  const renderActiveView = () => {
    switch (activeTab) {
      case 'today':
        return (
          <CampsView
            onNavigateToCalendar={(eventId) => {
              if (eventId) {
                const url = new URL(window.location.href)
                url.searchParams.set('event', eventId)
                window.history.pushState({}, '', url.toString())
              }
              setActiveTab('calendar')
            }}
            onNavigateToHealth={() => setActiveTab('health')}
          />
        )
      case 'calendar':
        return <CalendarView />
      case 'sports':
        return <SportsView />
      case 'health':
        return <HealthView />
      case 'team':
        return <TeamView />
      case 'evaluations':
        return <EvaluationHub />
      case 'dashboard':
        return (
          <DashboardView
            onNavigateToEvaluations={() => setActiveTab('evaluations')}
            onNavigateToHealth={() => setActiveTab('health')}
            onNavigateToStats={() => setActiveTab('sports')}
          />
        )
      case 'dashboard-preview':
        return (
          <DashboardPreviewView
            onNavigateToEvaluations={() => setActiveTab('evaluations')}
            onNavigateToHealth={() => setActiveTab('health')}
            onNavigateToStats={() => setActiveTab('sports')}
            onBackToApp={() => setActiveTab('today')}
          />
        )
      case 'admin':
        return <UsersAndPermissionsView />
      case 'auth':
        return <AuthStatusView />
      default:
        return <CampsView />
    }
  }

  return (
    <AppLayout activeTab={activeTab} onSelectTab={setActiveTab}>
      {renderActiveView()}
    </AppLayout>
  )
}

export const App: React.FC = () => {
  useMobileViewport()
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App
